import { Material } from './Material';
import { InterpolatedProperty, SimpleSettingsProperty, SettingsProperty } from '../properties';
import { Color } from '../types';
import * as THREE from 'three';
import { POIData } from '../poi/POIData';
import { AppSettingsData } from '../core/AppSettingsData';
import { Logger } from '../core/Logger';
import { SceneSettingsData } from '../core/SceneSettingsData';



/**
 * An abstract base class for all Physically-Based-Rendering materials.
 * It encapsulates a THREE.MeshStandardMaterial and manages common PBR properties
 * like roughness, metalness, opacity, etc., including their listeners.
 */
export class PBRMaterial extends Material {

    // --- Core PBR Properties ---
    public readonly color: InterpolatedProperty<Color>;
    public readonly opacity: InterpolatedProperty<number>;
    public readonly metalness: InterpolatedProperty<number>;
    public readonly roughness: InterpolatedProperty<number>;
    public readonly emissive: InterpolatedProperty<Color>;
    public readonly emissiveIntensity: InterpolatedProperty<number>;
    
    // --- Rendering Control Properties ---
    public readonly wireframe: SimpleSettingsProperty<boolean>;
    public readonly depthTest: SimpleSettingsProperty<boolean>;
    public readonly depthWrite: SimpleSettingsProperty<boolean>;
    public readonly transparent: SimpleSettingsProperty<boolean>;

    // The actual, underlying Three.js material instance is now lazily initialized.
    protected _threeMaterial: THREE.MeshStandardMaterial | null = null;

    protected _poiData: POIData | null = null;
    

    private static readonly _white = new THREE.Color(0xffffff);

    constructor(
        name: string = 'PBRMaterial',
        poiData: POIData
    ) {
        super('pbr', name);

        this._poiData = poiData;

        const pbrGroup = "PBR";
        const renderGroup = "Rendering";
        const emissiveGroup = "Emissive";


        // 🎯 Create properties with direct values first
        this.color = new InterpolatedProperty("Color", null, new Color(1, 1, 1), 0.2, pbrGroup);
        this.opacity = new InterpolatedProperty("Opacity", null, 1.0, 0.2, pbrGroup).setBounds(0, 1, 0.01);
        this.metalness = new InterpolatedProperty("Metalness", null, 0.0, 0.2, pbrGroup).setBounds(0, 1, 0.01);
        this.roughness = new InterpolatedProperty("Roughness", null, 0.5, 0.2, pbrGroup).setBounds(0, 1, 0.01);
        
        this.emissive = new InterpolatedProperty("Emissive Color", null, new Color(0, 0, 0), 0.2, emissiveGroup);
        this.emissiveIntensity = new InterpolatedProperty("Emissive Intensity", null, 0.0, 0.2, emissiveGroup).withBounds(0, 10, 0.1);
        
        this.wireframe = new SimpleSettingsProperty("Wireframe", null, false, renderGroup);
        this.depthTest = new SimpleSettingsProperty("Depth Test", null, true, renderGroup);
        this.depthWrite = new SimpleSettingsProperty("Depth Write", null, true, renderGroup);
        this.transparent = new SimpleSettingsProperty("Transparent", null, false, renderGroup);

        // 🎯 Set up parent hierarchies if data objects are provided
        this.setupParentHierarchies(poiData);

        // The creation of the THREE material is deferred to the first time getThreeMaterial() is called.
    }

    private setupParentHierarchies(poiData: POIData): void {
        // Set up parent relationships for basic POI properties only
        this.color.addParent(poiData.color);
        this.opacity.addParent(poiData.opacity);
        
        // 🎯 Use global SceneSettingsData for properties not in POIData
        const sceneSettings = SceneSettingsData.Instance;
        this.emissive.addParent(sceneSettings.emissive);
        this.emissiveIntensity.addParent(sceneSettings.emissiveIntensity);
        this.wireframe.addParent(sceneSettings.wireframe);
        this.depthTest.addParent(sceneSettings.depthTest);
        this.depthWrite.addParent(sceneSettings.depthWrite);
        this.transparent.addParent(sceneSettings.transparent);
    }

    /**
     * 🎯 CLEAN: Setup this material to use primary roughness/metalness properties
     */
    public setupAsPrimary(poiData: POIData): void {
        this.metalness.addParent(poiData.primaryMetalness);
        this.roughness.addParent(poiData.primaryRoughness);
    }

    /**
     * 🎯 CLEAN: Setup this material to use secondary roughness/metalness properties  
     */
    public setupAsSecondary(poiData: POIData): void {
        this.metalness.addParent(poiData.secondaryMetalness);
        this.roughness.addParent(poiData.secondaryRoughness);
    }
    
    // --- Abstract Method Implementation ---

    public override onUpdate(deltaTime: number): void {
        super.onUpdate(deltaTime);
        
        if (!this._threeMaterial) {
            //Logger.Instance.Debug(`[PBRMaterial] ${this.name}: No THREE material found, skipping update`);
            return;
        }

        ////Logger.Instance.Debug(`[PBRMaterial] ${this.name}: Updating material of type ${this._threeMaterial.constructor.name}`);

        // Sync renderBackface with global settings consideration
        const newSide = this.getAppropriateThreeSide();
        if (this._threeMaterial.side !== newSide) {
            this._threeMaterial.side = newSide;
        }

        // --- Explicit synchronization for all core PBR properties ---
        
        // Roughness (only for MeshStandardMaterial)
        const newRoughness = this.roughness.value;
        if (newRoughness !== null && 'roughness' in this._threeMaterial && this._threeMaterial.roughness !== newRoughness) {
            this._threeMaterial.roughness = newRoughness;
        }

        // Metalness (only for MeshStandardMaterial)
        const newMetalness = this.metalness.value;
        if (newMetalness !== null && 'metalness' in this._threeMaterial && this._threeMaterial.metalness !== newMetalness) {
            this._threeMaterial.metalness = newMetalness;
        }

        // Opacity
        const newOpacity = this.opacity.value;
        if (newOpacity !== null && this._threeMaterial.opacity !== newOpacity) {
            this._threeMaterial.opacity = newOpacity;
        }

        // Determine and sync transparency: true if the property is set OR opacity is < 1
        const shouldBeTransparent = this.transparent.value || (newOpacity ?? 1.0) < 1.0;
        if (this._threeMaterial.transparent !== shouldBeTransparent) {
            //console.log(`🔄 [DEBUG] ${this.name}: Updating THREE.js transparency from ${this._threeMaterial.transparent} to ${shouldBeTransparent} (transparent.value=${this.transparent.value}, opacity=${newOpacity})`);
            this._threeMaterial.transparent = shouldBeTransparent;
        }
        
        // Determine and sync depthWrite: uses the property, but defaults to off if transparent
        const shouldDepthWrite = this.depthWrite.value ?? !shouldBeTransparent;
        if (this._threeMaterial.depthWrite !== shouldDepthWrite) {
            this._threeMaterial.depthWrite = shouldDepthWrite;
        }

        // Color - if a texture map exists, set color to white to avoid tinting.
        // Otherwise, use the specified material color.
        if (this._threeMaterial.map) {
            if (!this._threeMaterial.color.equals(PBRMaterial._white)) {
                this._threeMaterial.color.set(PBRMaterial._white);
            }
        } else {
            const newColor = this.color.value;
            if (newColor !== null && !this._threeMaterial.color.equals(Color.toThree(newColor))) {
                this._threeMaterial.color.copy(Color.toThree(newColor));
            }
        }
        
        // Emissive Color (only for MeshStandardMaterial)
        const newEmissive = this.emissive.value;
        if (newEmissive !== null && 'emissive' in this._threeMaterial && this._threeMaterial.emissive && !this._threeMaterial.emissive.equals(Color.toThree(newEmissive))) {
            this._threeMaterial.emissive.copy(Color.toThree(newEmissive));
        }

        // Emissive Intensity (only for MeshStandardMaterial)
        const newEmissiveIntensity = this.emissiveIntensity.value;
        if (newEmissiveIntensity !== null && 'emissiveIntensity' in this._threeMaterial && this._threeMaterial.emissiveIntensity !== newEmissiveIntensity) {
            this._threeMaterial.emissiveIntensity = newEmissiveIntensity;
        }
    }
    
    /**
     * Determines the appropriate Three.js side value based on renderBackface property and global settings
     */
    protected getAppropriateThreeSide(): THREE.Side {
        // For base PBRMaterial, just use the renderBackface property
        // TexturedMaterial subclass will override this with more sophisticated logic
        const shouldRenderBackside = this.renderBackface.value ?? false;
        return shouldRenderBackside ? THREE.DoubleSide : THREE.FrontSide;
    }

    public getThreeMaterial(): THREE.Material {
        if (!this._threeMaterial) {
            this._threeMaterial = new THREE.MeshStandardMaterial({
                color: Color.toThree(this.color.value),
                opacity: this.opacity.value ?? 1.0,
                metalness: this.metalness.value ?? 0.0,
                roughness: this.roughness.value ?? 0.5,
                emissive: Color.toThree(this.emissive.value),
                transparent: (this.opacity.value ?? 1.0) < 1.0,
                side: this.getAppropriateThreeSide()
            });

            // The onUpdate loop is now the single source of truth for syncing properties.
            // All event-based listeners have been removed to ensure a pure pull-based model.

        }
        return this._threeMaterial;
    }
} 